import { format } from 'date-fns';
import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { getI18n } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { BpStatus, ITableCell, ITableRow, SyncStatus } from '../../components/app';
import { BpStatusSort, PatientsNameRow, PatientsSort } from '../../components/app/patients/components';
import { BpTypes, SyncStates } from '../../constants';
import { BpType, FetchStatus, PatientsSortBy, SortType, SyncState } from '../../enums';
import { IPatient } from '../../interfaces';
import { formatInStandard, parseDate, useDispatch, useSelector } from '../../utils';
import { useOrganization } from '../organization';
import {
  fetchPatients,
  resetPatients,
  updatePatientsBpStatusFilter,
  updatePatientsNewUserFilter,
  updatePatientsSearchText,
  updatePatientsSort,
  updatePatientsSyncStatusFilter,
} from './actions';
import { PatientsTableColumns } from './constants';
import { PatientsTableColumn } from './enums';
import { bpStatusFilterInitialState, syncStatusFilterInitialState } from './reducers';
import {
  selectPatientById,
  selectPatients,
  selectPatientsBpStatusFilter,
  selectPatientsFilters,
  selectPatientsNewUserFilter,
  selectPatientsSearchText,
  selectPatientsSort,
  selectPatientsSyncStatusFilter,
} from './selectors';
import { IPatientsBpStatusFilter, IPatientsSort, IPatientsSyncStatusFilter } from './types';
import { getPatientSyncState, getPatientSyncStateText } from './utils';

export const usePatients = () => useSelector(selectPatients);
export const usePatientsSearchText = () => useSelector(selectPatientsSearchText);
export const usePatientsFilters = () => useSelector(selectPatientsFilters);
export const usePatientsBpStatusFilters = () => useSelector(selectPatientsBpStatusFilter);
export const usePatientsSyncStatusFilters = () => useSelector(selectPatientsSyncStatusFilter);
export const usePatientsNewUserFilters = () => useSelector(selectPatientsNewUserFilter);
export const usePatientsSort = () => useSelector(selectPatientsSort);
export const usePatientById = (patientId: number) => useSelector((state) => selectPatientById(state, patientId));

export function useResetPatients() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetPatients());
  }, [dispatch]);
}

export function useUpdatePatientsSearchText() {
  const dispatch = useDispatch();

  return useCallback(
    (searchText?: string) => {
      dispatch(updatePatientsSearchText({ searchText: searchText ?? '' }));
    },
    [dispatch],
  );
}

export function useUpdatePatientsBpStatusFilter() {
  const dispatch = useDispatch();

  return useCallback(
    (filter?: IPatientsBpStatusFilter) => {
      dispatch(updatePatientsBpStatusFilter({ filters: filter ?? bpStatusFilterInitialState }));
    },
    [dispatch],
  );
}

export function useUpdatePatientsSyncStatusFilter() {
  const dispatch = useDispatch();

  return useCallback(
    (filter?: IPatientsSyncStatusFilter) => {
      dispatch(updatePatientsSyncStatusFilter({ filters: filter ?? syncStatusFilterInitialState }));
    },
    [dispatch],
  );
}

export function useUpdatePatientsNewUserFilter() {
  const dispatch = useDispatch();

  return useCallback(
    (newUser: boolean) => {
      dispatch(updatePatientsNewUserFilter({ newUser }));
    },
    [dispatch],
  );
}

export function useUpdateSort() {
  const dispatch = useDispatch();

  return useCallback(
    (sort: IPatientsSort) => {
      dispatch(updatePatientsSort({ sort }));
    },
    [dispatch],
  );
}

export function useFetchPatients() {
  const dispatch = useDispatch();
  const patients = usePatients();

  useEffect(() => {
    if (patients.status === FetchStatus.IDLE && patients.patients.length === 0) {
      dispatch(fetchPatients());
    }
  }, []);
}

export function usePatientByUrlParam(): IPatient | undefined {
  const { patientId = '' } = useParams();
  const patient = usePatientById(parseInt(patientId, 10));

  return useMemo(() => patient, [patient]);
}

export function useTableRows(): ITableRow[] {
  const patients = useFilteredPatients();
  const { organization } = useOrganization();

  return useMemo(() => {
    return patients.map((patient: IPatient) => ({
      id: patient.id,
      elements: PatientsTableColumns.map(({ column, minWidth }) => {
        switch (column) {
          case PatientsTableColumn.NAME:
            return {
              element: createElement(PatientsNameRow, { patient }),
              minWidth,
            };
          case PatientsTableColumn.BIRTHDAY:
            return {
              element: createElement(
                'span',
                {},
                `${formatInStandard(parseDate(patient.birthday), 'd MMM yyyy', 'MM/dd/yyyy')}`,
              ),
              minWidth,
            };
          case PatientsTableColumn.ORGANIZATION:
            return {
              element: createElement('span', {}, organization?.name),
              minWidth,
            };
          case PatientsTableColumn.SYNC_STATE:
            return {
              element: createElement(SyncStatus, { patient, showSubtitle: false }),
              minWidth,
            };
          case PatientsTableColumn.BP_TYPE:
            return {
              element: createElement(
                'div',
                { className: 'flex justify-end w-full' },
                createElement(BpStatus, { patient, isUS: patient?.countryKey === 'usa' }),
              ),
              minWidth,
            };
        }
      }),
    }));
  }, [patients, organization]);
}

export function useTableHeaders(): ITableCell[] {
  return PatientsTableColumns.map(({ column, minWidth }) => {
    switch (column) {
      case PatientsTableColumn.NAME:
        return {
          element: createElement(PatientsSort, { by: PatientsSortBy.NAME }),
          minWidth,
        };
      case PatientsTableColumn.SYNC_STATE:
        return {
          element: createElement(PatientsSort, { by: PatientsSortBy.SYNC_STATE }),
          minWidth,
        };
      case PatientsTableColumn.BP_TYPE:
        return {
          element: createElement('div', { className: 'flex justify-end w-full' }, createElement(BpStatusSort)),
          minWidth,
        };
      default: {
        const keyName = getI18n().t(`patients.table.${column}`);
        return {
          element: createElement('span', {}, keyName),
          minWidth,
        };
      }
    }
  });
}

export function useFilteredPatients(): IPatient[] {
  const patients = usePatients();
  const searchText = usePatientsSearchText().toLowerCase();
  const filters = usePatientsFilters();
  const sort = usePatientsSort();
  const { organization } = useOrganization();

  const patientsSorted = useMemo(() => {
    if (sort.type === SortType.IDLE) {
      return patients.patients;
    }

    return [...patients.patients].sort((a: IPatient, b: IPatient) => {
      if (sort.type === SortType.ASC) {
        switch (sort.by) {
          case PatientsSortBy.NAME:
            return a.lastName.localeCompare(b.lastName);
          case PatientsSortBy.SYNC_STATE:
            return SyncStates.indexOf(getPatientSyncState(a)) - SyncStates.indexOf(getPatientSyncState(b));
          case PatientsSortBy.BP_TYPE:
            return BpTypes.indexOf(a.bloodPressure.status) - BpTypes.indexOf(b.bloodPressure.status);
          default:
            return 0;
        }
      } else {
        switch (sort.by) {
          case PatientsSortBy.NAME:
            return b.lastName.localeCompare(a.lastName);
          case PatientsSortBy.SYNC_STATE:
            return SyncStates.indexOf(getPatientSyncState(b)) - SyncStates.indexOf(getPatientSyncState(a));
          case PatientsSortBy.BP_TYPE:
            return BpTypes.indexOf(b.bloodPressure.status) - BpTypes.indexOf(a.bloodPressure.status);
          default:
            return 0;
        }
      }
    });
  }, [patients.patients, sort]);

  const patientsFilteredBySearch: IPatient[] = useMemo(() => {
    return patientsSorted.filter((patient: IPatient) => {
      const patientDataToSearch = {
        firstName: patient.firstName.toLowerCase(),
        lastName: patient.lastName.toLowerCase(),
        birthdate: format(parseDate(patient.birthday), 'd MMM yyyy').toLowerCase(),
        bloodPressureStatus: patient.bloodPressure.status.toLowerCase().replace('_', ' '),
        patientStatus: getPatientSyncStateText(patient).toLowerCase(),
        organization: organization?.name.toLowerCase() ?? '',
      };
      const values: string[] = Object.values(patientDataToSearch);
      return values.some((value: string) => value.includes(searchText));
    });
  }, [patientsSorted, searchText, organization]);

  const patientsFilteredByFilters: IPatient[] = useMemo(() => {
    const activeBpStatusFilters: BpType[] = (Object.keys(filters.bpType) as BpType[]).filter(
      (key) => filters.bpType[key],
    );

    const activeSyncStatusFilters: SyncState[] = (Object.keys(filters.syncState) as SyncState[]).filter(
      (key) => filters.syncState[key],
    );

    return patientsFilteredBySearch.filter(
      (patient: IPatient) =>
        (!activeBpStatusFilters.length || activeBpStatusFilters.includes(patient.bloodPressure.status)) &&
        (!activeSyncStatusFilters.length || activeSyncStatusFilters.includes(getPatientSyncState(patient))) &&
        (!filters.newUser || patient.newUser),
    );
  }, [patientsFilteredBySearch, filters]);

  return patientsFilteredByFilters;
}

export function useAnyFiltersActive() {
  const patients = usePatients();
  const [isAnyActive, setIsAnyActive] = useState(false);

  useEffect(() => {
    const isAnyFilterActive = Object.values({
      ...patients.filters.bpType,
      ...patients.filters.syncState,
      newUser: patients.filters.newUser,
    }).some((filter) => !!filter);

    if (patients.searchText !== '' || isAnyFilterActive) {
      setIsAnyActive(true);
    } else {
      setIsAnyActive(false);
    }
  }, [patients.searchText, patients.filters]);

  return isAnyActive;
}
